import { Alert, Box, Button, Card, CardContent, Typography } from "@mui/material";
import { AccountCircle, ContactSupport } from "@mui/icons-material";
import { useBranding } from "../../branding/BrandingProvider";
import SessionService from "../../services/SessionService";

/**
 * What a signed-in account with no role sees, wherever it lands.
 *
 * Staff accounts can no longer be created or edited down to zero roles (the server refuses both), so
 * this is a safety net for legacy accounts. It used to live only inside RequireAuth, while the routes
 * a user actually lands on (/dashboard and the other ByRole / RequireAudience paths) rendered a 404
 * for a roleless account — so the explanation existed but almost nobody saw it.
 */
export default function NoRolesAssigned() {
  const brand = useBranding();
  // Only a real, configured address: the brand rule is never to show invented contact details.
  const supportEmail = brand.support?.supportEmail ?? brand.support?.email;

  const signOut = async () => {
    // The real logout, which ends the session server-side too. This card used to clear only the
    // browser's storage, leaving a live session behind.
    await SessionService.getInstance().logout();
    window.location.href = "/login";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: 600, width: "100%" }}>
        <CardContent sx={{ textAlign: "center", p: 4 }}>
          <AccountCircle sx={{ fontSize: 80, color: "warning.main", mb: 2 }} />

          <Typography variant="h4" gutterBottom sx={{ color: "warning.main" }}>
            Access Restricted
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            No Roles Assigned
          </Typography>

          <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="body1" gutterBottom>
              Your account does not have any assigned roles. You need at least one role to access
              this application.
            </Typography>
            <Typography variant="body2">
              Please contact your system administrator to assign appropriate roles to your
              account.
            </Typography>
          </Alert>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            {supportEmail && (
              <Button
                variant="contained"
                startIcon={<ContactSupport />}
                href={`mailto:${supportEmail}?subject=Role Assignment Request`}
              >
                Contact Support
              </Button>
            )}
            <Button variant="outlined" onClick={signOut}>
              Sign Out
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
